import { useMemo, useState, useCallback, useEffect } from "react";
import { type FormikProps } from "formik";
import Dropdown from "../../../../../../../components/Dropdown/Dropdown";
import ConcurrentLogoIcon from "../../../../../../../assets/svg/concurrent-logo-icon";
import PowerBiLogoPng from "../../../../../../../assets/images/power-bi-logo.png";
import HubspotLogoIcon from "../../../../../../../assets/svg/hubspot-logo-icon";
import FeatureRequestStarIcon from "../../../../../../../assets/svg/feature-request-star-icon";
import AddUserIcon from "../../../../../../../assets/svg/add-user-icon";
import PrismLogoPng from "../../../../../../../assets/images/prism-logo.png";
import SettingsIcon from "../../../../../../../assets/svg/settings-icon";
import SearchIcon from "../../../../../../../assets/svg/search-icon";
import type { CreateTicketFormValues } from "../utils/types";
import type { HelpdeskCustomFieldDefinition } from "../../TicketDetails/components/TicketOverview/utils/helpdeskCustomFields";
import { getEffectiveDropdownOptions } from "../../TicketDetails/components/TicketOverview/utils/helpdeskCustomFields";
import { FEATURE_REQUESTS_SYSTEM_NAME } from "../utils/constants";
import VerbafloLogoIcon from "../../../../../../../assets/images/verbaflo-logo.png";

const DROPDOWN_TRIGGER_CLASS = "!bg-pure-white dark:!bg-pure-white-dk";
const DROPDOWN_LABEL_CLASS =
  "text-md font-normal text-heading-text dark:text-heading-text-dk";
const DROPDOWN_PLACEHOLDER_CLASS =
  "text-gray-placeholder dark:text-gray-placeholder-dk text-md font-normal";
const DROPDOWN_OPTION_CLASS =
  "border-b border-border-gray dark:border-border-gray-dk";

const SYSTEM_ICON_MAP: Record<string, React.ReactNode> = {
  concurrent: <ConcurrentLogoIcon className="w-4 h-3.5" />,
  "power-bi": <img src={PowerBiLogoPng} alt="Power BI" className="w-4 h-3.5" />,
  hubspot: <HubspotLogoIcon className="w-4 h-4" />,
  prism: <img src={PrismLogoPng} alt="PRISM" className="w-4 h-3.5" />,
  "feature-requests": <FeatureRequestStarIcon className="w-4 h-3.5" />,
  "new-user-access": (
    <AddUserIcon className="w-4 h-3.5 text-verba-blue dark:text-verba-blue-dk" />
  ),
  verbaflo: <img src={VerbafloLogoIcon} alt="VerbaFlo" className="w-4 h-4" />,
};

function getSystemIcon(label: string): React.ReactNode | null {
  const key = label?.toLowerCase().replace(/\s+/g, "-");
  return (
    SYSTEM_ICON_MAP[key] ?? (
      <SettingsIcon className="w-4 h-4 text-verba-indigo dark:text-verba-indigo-dk" />
    )
  );
}

function dropdownOption(label: string) {
  const icon = getSystemIcon(label);
  const content = (
    <span className="flex items-center gap-2 text-md text-heading-text dark:text-heading-text-dk">
      {icon}
      {label}
    </span>
  );
  return {
    value: label,
    content,
    selectedOptionContent: content,
  };
}

const DIRECT_ISSUE_CATEGORY_MAP: Record<string, string> = {
  sales: "Sales",
  marketing: "Marketing",
};

const SystemAndIssueTypeFields = ({
  formik,
  systemNameField,
  issueTypeField,
  selectedTicketCategory,
}: {
  formik: FormikProps<CreateTicketFormValues>;
  systemNameField: HelpdeskCustomFieldDefinition | null;
  issueTypeField: HelpdeskCustomFieldDefinition | null;
  selectedTicketCategory: string;
}) => {
  const directIssueSystemName = DIRECT_ISSUE_CATEGORY_MAP[selectedTicketCategory] ?? null;
  const selectedSystem = formik?.values?.systemName;

  useEffect(() => {
    if (directIssueSystemName && selectedSystem !== directIssueSystemName) {
      formik?.setFieldValue("systemName", directIssueSystemName);
      formik?.setFieldValue("issueType", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directIssueSystemName]);

  const [systemSearch, setSystemSearch] = useState("");
  const [issueSearch, setIssueSearch] = useState("");

  const allSystemOptions = useMemo(
    () => systemNameField?.values_for_dropdown ?? [],
    [systemNameField]
  );

  const filteredSystemOptions = useMemo(() => {
    const query = systemSearch.trim().toLowerCase();
    const filtered = query
      ? allSystemOptions.filter((opt) => opt.toLowerCase().includes(query))
      : allSystemOptions;
    return filtered.map(dropdownOption);
  }, [allSystemOptions, systemSearch]);

  const handleSystemChange = useCallback(
    (value: string) => {
      formik?.setFieldValue("systemName", value);
      formik?.setFieldValue("issueType", "");
      if (value === FEATURE_REQUESTS_SYSTEM_NAME) {
        formik?.setFieldValue("priority", undefined);
      }
      setSystemSearch("");
    },
    [formik]
  );

  const allIssueOptions = useMemo(() => {
    if (!issueTypeField) return [];
    return getEffectiveDropdownOptions(issueTypeField, {
      "system-name": selectedSystem,
    });
  }, [issueTypeField, selectedSystem]);

  const filteredIssueOptions = useMemo(() => {
    const query = issueSearch.trim().toLowerCase();
    const filtered = query
      ? allIssueOptions.filter((opt) => opt.toLowerCase().includes(query))
      : allIssueOptions;
    return filtered.map((label) => {
      const content = (
        <span className="text-md text-heading-text dark:text-heading-text-dk">
          {label}
        </span>
      );
      return { value: label, content, selectedOptionContent: content };
    });
  }, [allIssueOptions, issueSearch]);

  const handleIssueChange = useCallback(
    (value: string) => {
      formik?.setFieldValue("issueType", value);
      setIssueSearch("");
    },
    [formik]
  );

  const hasSelectedSystem = Boolean(selectedSystem);
  const hasIssueOptions = allIssueOptions.length > 0;
  const isFeatureRequest = selectedSystem === FEATURE_REQUESTS_SYSTEM_NAME;
  const isDirectIssueMode = Boolean(directIssueSystemName);

  const issueTypePlaceholder = isDirectIssueMode
    ? "Select issue type"
    : !hasSelectedSystem
      ? "Please select a system name first"
      : !hasIssueOptions
        ? `No issue types available for "${selectedSystem}"`
        : "Select issue type";

  const isIssueTypeDisabled = isDirectIssueMode ? false : !hasSelectedSystem;

  const systemSearchMenu = (
    <div className="flex flex-col border-b border-border-gray dark:border-border-gray-dk">
      <div className="flex items-center gap-1.5 px-2.5 py-2">
        <SearchIcon className="w-3.5 h-3.5 text-gray-placeholder dark:text-gray-placeholder-dk shrink-0" />
        <input
          type="text"
          value={systemSearch}
          onChange={(e) => setSystemSearch(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          placeholder="Search system names..."
          className="flex-1 text-sm bg-transparent border-none outline-none text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk"
          autoFocus
        />
      </div>
    </div>
  );

  const issueSearchMenu = (
    <div className="flex flex-col border-b border-border-gray dark:border-border-gray-dk">
      <div className="flex items-center gap-1.5 px-2.5 py-2">
        <SearchIcon className="w-3.5 h-3.5 text-gray-placeholder dark:text-gray-placeholder-dk shrink-0" />
        <input
          type="text"
          value={issueSearch}
          onChange={(e) => setIssueSearch(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          placeholder="Search issue types..."
          className="flex-1 text-sm bg-transparent border-none outline-none text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk"
          autoFocus
        />
      </div>
    </div>
  );

  return (
    <div className={`grid ${isDirectIssueMode ? "grid-cols-1" : "grid-cols-2"} gap-4`}>
      {!isDirectIssueMode && (
        <div className="flex flex-col gap-1">
          <Dropdown
            id="system-name-dropdown"
            label="System Name"
            options={filteredSystemOptions}
            value={selectedSystem}
            onChange={handleSystemChange}
            placeholder="Select system"
            ariaLabel="Select system"
            className="w-full"
            triggerClassName={DROPDOWN_TRIGGER_CLASS}
            selectedOptionContainerClassName="h-[36px] w-full"
            labelClassName={DROPDOWN_LABEL_CLASS}
            placeholderClassName={DROPDOWN_PLACEHOLDER_CLASS}
            dropdownClassName="!max-h-[200px] mt-1"
            optionClassName={DROPDOWN_OPTION_CLASS}
            dropdownMenuTitle={systemSearchMenu}
          />
          {formik?.touched?.systemName && formik?.errors?.systemName && (
            <span className="text-xs text-verba-red-alert dark:text-verba-red-alert-dk">
              {formik?.errors?.systemName}
            </span>
          )}
        </div>
      )}

      {!isFeatureRequest && (
        <div className="flex flex-col gap-1">
          <Dropdown
            id="issue-type-dropdown"
            label="Issue Type"
            options={filteredIssueOptions}
            value={formik?.values?.issueType}
            onChange={handleIssueChange}
            placeholder={issueTypePlaceholder}
            ariaLabel="Select issue type"
            className={`w-full ${isIssueTypeDisabled ? "pointer-events-none opacity-60" : ""}`}
            triggerClassName={DROPDOWN_TRIGGER_CLASS}
            selectedOptionContainerClassName="h-[36px] w-full"
            labelClassName={DROPDOWN_LABEL_CLASS}
            placeholderClassName={DROPDOWN_PLACEHOLDER_CLASS}
            dropdownClassName="!max-h-[200px] mt-1"
            optionClassName={DROPDOWN_OPTION_CLASS}
            dropdownMenuTitle={hasSelectedSystem ? issueSearchMenu : undefined}
          />
          {formik?.touched?.issueType && formik?.errors?.issueType && (
            <span className="text-xs text-verba-red-alert dark:text-verba-red-alert-dk">
              {formik?.errors?.issueType}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default SystemAndIssueTypeFields;
